import { InfoCircleOutlined } from '@ant-design/icons';
import { Divider, Select, Skeleton, Tooltip } from 'antd';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  listTemplateOverrides,
  listTemplates,
} from '../../store/actions/settings';
import { TemplateEditModal } from './TemplateEditModal';
import { useAuth } from '@frontegg/react';
import { analytics } from 'src/services';
import { useIsOP } from 'src/hooks/misc';

const SummaryLoading = () => {
  return (
    <div className="settings-skeleton">
      <Skeleton paragraph={{ rows: 1, style: { height: 16 } }} />
    </div>
  );
};

const TemplateSelectDropdown = ({
  encounterPhase,
  currentTemplate,
  setCurrentTemplate,
  templateOverridesId = null,
  templateId = null,
  allowInlineEdit = false,
  isControlled = false,
  fromIsSettings = false,
  setNextWorkingTemplate,
  restrictTemplates = false,
  selectProps = {},
  setSelectedDefaultTemplate = () => {},
  encounterDescription = null,
}) => {
  const isOP = useIsOP();
  const dispatch = useDispatch();
  const {
    templateList,
    templateOverridesList,
    isTemplateListLoading,
    isTemplateOverridesListLoading,
  } = useSelector((state) => state?.settingsState);

  const isLoading = isTemplateListLoading || isTemplateOverridesListLoading;
  const { currentPractitioner, currentPractitionerSettings } = useSelector(
    (state) => state?.practitionerState
  );
  const { getUuid } = useSelector((state) => state?.appointmentState);

  const isMobile = window.innerWidth < 1260;
  const { user } = useAuth();
  const [checkDropdownOpen, setCheckDropdownOpen] = useState(false);
  const [templateInEditModal, setTemplateInEditModal] = useState(null);
  const userHasSelectedRef = useRef(false);

  let selectedTemplateOverridesId;
  let selectedTemplateId;

  if (restrictTemplates) {
    selectedTemplateOverridesId = null;
    selectedTemplateId = null;
  } else if (templateId || templateOverridesId) {
    selectedTemplateOverridesId = templateOverridesId;
    selectedTemplateId = templateId;
  } else {
    if (encounterPhase == 'in-visit') {
      selectedTemplateOverridesId =
        currentPractitionerSettings?.ambient_in_visit_template_overrides_id;
      selectedTemplateId =
        currentPractitionerSettings?.ambient_in_visit_template_id;
    } else if (encounterPhase == 'pre-chart') {
      selectedTemplateOverridesId =
        currentPractitionerSettings?.ambient_pre_chart_template_overrides_id;
      selectedTemplateId =
        currentPractitionerSettings?.ambient_pre_chart_template_id;
    } else if (encounterPhase == 'after-visit') {
      selectedTemplateOverridesId =
        currentPractitionerSettings?.ambient_after_visit_template_overrides_id;
      selectedTemplateId =
        currentPractitionerSettings?.ambient_after_visit_template_id;
    }
  }

  const apptType = getUuid?.appointment_details?.appointment_type;

  const defaultTemplateName = useMemo(() => {
    if (encounterPhase == 'in-visit') {
      if (restrictTemplates) {
        return (
          currentPractitioner?.org_settings
            ?.default_combo_in_visit_template_name?.[apptType] ||
          (apptType === 'Intake' ? 'h&p' : 'soap')
        );
      } else {
        return (
          currentPractitioner?.org_settings?.default_in_visit_template_name ||
          'soap'
        );
      }
    } else if (encounterPhase == 'pre-chart') {
      return (
        currentPractitioner?.org_settings?.default_pre_chart_template_name ||
        'pre-charting'
      );
    } else if (encounterPhase == 'after-visit') {
      return (
        currentPractitioner?.org_settings?.default_after_visit_template_name ||
        'after-visit'
      );
    }
  }, [
    apptType,
    encounterPhase,
    restrictTemplates,
    currentPractitioner?.org_settings,
  ]);

  useLayoutEffect(() => {
    dispatch(listTemplates(encounterPhase, currentPractitioner?.org_uuid));
    dispatch(listTemplateOverrides(encounterPhase));
  }, [encounterPhase]);

  const sendAnalyticsEvent = (message, data) => {
    const encounterPhaseMessage =
      encounterPhase === 'in-visit'
        ? 'In-Visit'
        : encounterPhase === 'pre-chart'
          ? 'Pre-Chart'
          : 'After-Visit';
    analytics.track(
      `${message} From ${encounterPhaseMessage} Template Dropdown`,
      { ...data, email: user?.email }
    );
  };

  useEffect(() => {
    if (
      isControlled &&
      (currentTemplate?.template_overrides_id || currentTemplate?.template_id)
    ) {
      return;
    }

    // Ensure both lists are loaded
    if (!isLoading) {
      // Search for an active template override
      const getFallbackTemplate = () =>
        templateList.find(
          (template) =>
            template.name?.toLowerCase() === defaultTemplateName?.toLowerCase()
        ) ||
        templateList.find(
          (template) => template.encounter_phase === encounterPhase
        );

      let chosenTemplate = null;
      if (isOP && encounterDescription && !userHasSelectedRef.current) {
        const lowerCaseEncounterDescription =
          encounterDescription.toLowerCase();
        if (lowerCaseEncounterDescription.includes('sick visit')) {
          chosenTemplate = templateList.find((template) =>
            template.name?.toLowerCase().includes('sick visit')
          );
        } else if (lowerCaseEncounterDescription.includes('well visit')) {
          chosenTemplate = templateList.find((template) =>
            template.name?.toLowerCase().includes('well visit')
          );
        }
      }

      if (chosenTemplate) {
        setCurrentTemplate(chosenTemplate);
      } else if (selectedTemplateOverridesId) {
        const activeTemplateOverride = templateOverridesList?.find(
          (template) =>
            template.template_overrides_id === selectedTemplateOverridesId
        );
        if (activeTemplateOverride) {
          setCurrentTemplate(activeTemplateOverride);
          if (fromIsSettings) {
            if (templateOverridesList.length > 1) {
              if (
                templateOverridesList[0].template_overrides_id !==
                selectedTemplateOverridesId
              ) {
                setNextWorkingTemplate(templateOverridesList[0]);
              } else {
                setNextWorkingTemplate(templateOverridesList[1]);
              }
            } else if (templateList.length > 0) {
              setNextWorkingTemplate(templateList[0]);
            } else {
              setNextWorkingTemplate({});
            }
          }
        } else {
          // If template override was deleted, reset currentTemplate
          setCurrentTemplate({});
        }
      } else if (selectedTemplateId) {
        const activeTemplate = templateList.find(
          (template) => template.template_id === selectedTemplateId
        );
        if (activeTemplate) {
          setCurrentTemplate(activeTemplate);
        } else {
          // If template was deleted, reset currentTemplate
          setCurrentTemplate({});
        }
      } else {
        const fallbackTemplate = getFallbackTemplate();
        if (fallbackTemplate) {
          setCurrentTemplate(fallbackTemplate);
        }
      }
    }
  }, [
    selectedTemplateOverridesId,
    selectedTemplateId,
    isLoading,
    defaultTemplateName,
    templateList,
    templateOverridesList,
    encounterDescription,
    isOP,
  ]);

  const onTemplateChange = (value) => {
    userHasSelectedRef.current = true;

    let selectedTemplate = templateList.find(
      (template) => template.template_id === value
    );

    if (selectedTemplate) {
      setCurrentTemplate(selectedTemplate, 'userSelected');
      setSelectedDefaultTemplate(true);

      sendAnalyticsEvent('Changed Template To A Pre-Built Template', {});
      return;
    }

    if (!selectedTemplate) {
      selectedTemplate = templateOverridesList.find(
        (template) => template.template_overrides_id === value
      );
    }

    if (selectedTemplate) {
      setCurrentTemplate(selectedTemplate, 'userSelected');
      setSelectedDefaultTemplate(false);

      sendAnalyticsEvent('Changed Template To A Custom Template', {});
      return;
    }
  };

  const handleEditTemplate = (template, event) => {
    event.preventDefault();
    event.stopPropagation();
    setTemplateInEditModal(template);
    setCheckDropdownOpen(false);
  };

  const renderTemplateOption = (template, type) => {
    const id =
      type === 'pre-built'
        ? template.template_id
        : template.template_overrides_id;

    const editButton = allowInlineEdit ? (
      <div className="font-medium flex items-center gap-2 flex-shrink-0">
        <button
          className="text-blue-500 hover:text-blue-700 hover:underline"
          onClick={(e) => {
            sendAnalyticsEvent('Clicked Edit', {
              template: template.name,
              type,
            });
            handleEditTemplate(template, e);
          }}
        >
          Edit
        </button>
      </div>
    ) : null;
    const getLabel = (isSelected) => {
      return (
        <div
          className={['flex items-center gap-2', isSelected ? 'mr-5' : '']
            .filter(Boolean)
            .join(' ')}
        >
          <span className="overflow-hidden text-ellipsis whitespace-nowrap">
            {template.name}
          </span>
          <Tooltip
            title={
              <div
                className={`overflow-auto cursor-pointer ${
                  template.body.sections && template.body.sections.length > 10
                    ? 'max-h-[40rem]'
                    : 'h-fit'
                } w-[15rem]`}
              >
                <div className="flex justify-between mb-4">
                  <span className="flex-grow font-medium overflow-hidden text-ellipsis whitespace-nowrap">
                    {template.name}
                  </span>
                  <span
                    style={{ right: 0, marginLeft: 'auto' }}
                    className="flex-shrink-0"
                  >
                    {editButton}
                  </span>
                </div>
                {template.body.sections && template.body.sections.length > 0 ? (
                  template.body.sections.map((section, index) => (
                    <p key={index}>
                      {section.name} <SummaryLoading />
                    </p>
                  ))
                ) : (
                  <p>No template sections</p>
                )}
              </div>
            }
            placement={'right'}
            arrow={false}
            overlayStyle={
              template.body.sections && template.body.sections.length > 10
                ? {
                    position: 'fixed',
                    top: '1.25rem',
                    bottom: '3rem',
                  }
                : {}
            }
          >
            <span style={{ cursor: 'default' }} className="text-gray-400">
              <InfoCircleOutlined />
            </span>
          </Tooltip>
        </div>
      );
    };
    return (
      <Select.Option
        key={id}
        name={template.name}
        value={id}
        label={getLabel(true)}
      >
        {!isMobile ? (
          <div className="font-sans flex justify-between gap-3">
            <div className="flex flex-col overflow-hidden">
              {getLabel()}
              <div className="text-xs text-gray-500 overflow-hidden text-ellipsis whitespace-nowrap">
                {template.description}
              </div>
            </div>
            {editButton}
          </div>
        ) : (
          ''
        )}
        {isMobile ? (
          <div className="font-sans">
            {template.name}
            <div style={{ color: 'grey', fontSize: 'smaller' }}>
              {template.description}
            </div>
          </div>
        ) : (
          ''
        )}
      </Select.Option>
    );
  };

  return (
    <div className="cursor-pointer relative">
      {templateInEditModal && (
        <TemplateEditModal
          encounterPhase={encounterPhase}
          template={templateInEditModal}
          onClose={() => setTemplateInEditModal(null)}
        />
      )}

      <Select
        id="template-select"
        placeholder="Select template"
        showSearch
        optionFilterProp="children"
        filterOption={(input, option) => {
          return (option?.name ?? '')
            .toLowerCase()
            .includes(input.toLowerCase());
        }}
        optionLabelProp="label"
        loading={isLoading}
        labelRender={({ label }) => {
          return isLoading ? 'Loading...' : label || 'Template not available';
        }}
        value={
          currentTemplate?.template_id || currentTemplate?.template_overrides_id
        }
        onChange={onTemplateChange}
        className={`w-full note-template`}
        disabled={restrictTemplates}
        open={checkDropdownOpen}
        onDropdownVisibleChange={(open) => {
          setCheckDropdownOpen(open);
        }}
        {...selectProps}
      >
        {templateOverridesList?.length > 0 && (
          <>
            <Select.Option
              disabled
              key="divider1"
              style={{ cursor: 'default', pointerEvents: 'none', padding: 0 }}
            >
              <Divider className="font-sans" plain>
                Custom note templates
              </Divider>
            </Select.Option>
            {templateOverridesList.map((template) =>
              renderTemplateOption(template, 'overrides')
            )}
          </>
        )}
        {templateList.length > 0 && (
          <>
            <Select.Option
              disabled
              key="divider2"
              style={{ cursor: 'default', pointerEvents: 'none', padding: 0 }}
            >
              <Divider className="font-sans" plain>
                Pre-built note templates
              </Divider>
            </Select.Option>
            {templateList.map((template) =>
              renderTemplateOption(template, 'pre-built')
            )}
          </>
        )}
      </Select>
    </div>
  );
};

export default TemplateSelectDropdown;
